import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { toast } from '../lib/toast';
import Sidebar from '../components/layout/Sidebar';
import { Settings as SettingsIcon, User, Building2, Bell, Shield, Palette, MessageCircle, Phone } from 'lucide-react';

const Settings = () => {
  const { user, api, updateProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    restaurantName: user?.restaurant_name || '',
    ownerName: user?.name || '',
    email: user?.email || '',
    notifications: {
      lowStock: true,
      newOrders: true,
      dailyReport: false
    },
    appearance: {
      darkMode: false,
      compactView: false
    }
  });

  // WhatsApp configuration state
  const [whatsappConfig, setWhatsappConfig] = useState({
    manager_whatsapp: '',
    notify_on_void: true,
    notify_on_edit: false
  });
  const [savingWhatsApp, setSavingWhatsApp] = useState(false);

  // Fetch WhatsApp config on mount
  useEffect(() => {
    const fetchWhatsAppConfig = async () => {
      try {
        const res = await api.get('/settings/whatsapp');
        setWhatsappConfig(res.data);
      } catch (error) {
        console.error('Error fetching WhatsApp config:', error);
      }
    };
    fetchWhatsAppConfig();
  }, [api]);

  const handleSaveWhatsApp = async () => {
    setSavingWhatsApp(true);
    try {
      const res = await api.put('/settings/whatsapp', whatsappConfig);
      setWhatsappConfig({
        manager_whatsapp: res.data.manager_whatsapp,
        notify_on_void: res.data.notify_on_void,
        notify_on_edit: res.data.notify_on_edit
      });
      toast.success('Configuración de WhatsApp guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSavingWhatsApp(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({
        name: settings.ownerName,
        restaurant_name: settings.restaurantName
      });
      toast.success('Configuración guardada correctamente');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-8 pt-20 md:pt-8">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-chivo font-extrabold" data-testid="settings-title">
              Configuración
            </h1>
            <p className="text-muted-foreground">
              Personaliza tu cuenta y preferencias del sistema
            </p>
          </div>

          {/* Profile Section */}
          <Card className="mb-6 border border-border/50">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2">
                <User className="w-5 h-5" />
                Perfil de Usuario
              </CardTitle>
              <CardDescription>
                Información básica de tu cuenta
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-accent/10 flex items-center justify-center">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
                  ) : (
                    <span className="text-2xl font-bold text-accent">
                      {user?.name?.charAt(0).toUpperCase() || 'U'}
                    </span>
                  )}
                </div>
                <div>
                  <p className="font-bold">{user?.name || 'Usuario'}</p>
                  <p className="text-sm text-muted-foreground">{user?.email}</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label htmlFor="ownerName">Nombre</Label>
                  <Input
                    id="ownerName"
                    value={settings.ownerName}
                    onChange={(e) => setSettings({ ...settings, ownerName: e.target.value })}
                    data-testid="settings-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <Input
                    id="email"
                    type="email"
                    value={settings.email}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    El correo no puede cambiarse
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Restaurant Section */}
          <Card className="mb-6 border border-border/50">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                Información del Restaurante
              </CardTitle>
              <CardDescription>
                Datos de tu establecimiento
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="restaurantName">Nombre del Restaurante</Label>
                <Input
                  id="restaurantName"
                  value={settings.restaurantName}
                  onChange={(e) => setSettings({ ...settings, restaurantName: e.target.value })}
                  data-testid="settings-restaurant-input"
                />
              </div>
            </CardContent>
          </Card>

          {/* Notifications Section */}
          <Card className="mb-6 border border-border/50">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notificaciones
              </CardTitle>
              <CardDescription>
                Configura las alertas del sistema
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Alerta de bajo stock</p>
                  <p className="text-sm text-muted-foreground">
                    Notificar cuando un producto tenga menos de 10 unidades
                  </p>
                </div>
                <Switch
                  checked={settings.notifications.lowStock}
                  onCheckedChange={(v) => setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, lowStock: v }
                  })}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Nuevas órdenes</p>
                  <p className="text-sm text-muted-foreground">
                    Sonido al recibir nuevas órdenes
                  </p>
                </div>
                <Switch
                  checked={settings.notifications.newOrders}
                  onCheckedChange={(v) => setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, newOrders: v }
                  })}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Reporte diario</p>
                  <p className="text-sm text-muted-foreground">
                    Recibir resumen de ventas por correo cada día
                  </p>
                </div>
                <Switch
                  checked={settings.notifications.dailyReport}
                  onCheckedChange={(v) => setSettings({
                    ...settings,
                    notifications: { ...settings.notifications, dailyReport: v }
                  })}
                />
              </div>
            </CardContent>
          </Card>

          {/* WhatsApp Notifications Section */}
          <Card className="mb-6 border border-green-200 bg-green-50/30">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                Notificaciones WhatsApp
              </CardTitle>
              <CardDescription>
                Configura las notificaciones automáticas al gerente
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="managerWhatsapp" className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  Número de WhatsApp del Gerente
                </Label>
                <Input
                  id="managerWhatsapp"
                  value={whatsappConfig.manager_whatsapp}
                  onChange={(e) => setWhatsappConfig({ ...whatsappConfig, manager_whatsapp: e.target.value })}
                  placeholder="+526221234567"
                  className="font-mono"
                  data-testid="whatsapp-number-input"
                />
                <p className="text-xs text-muted-foreground">
                  Formato: +52 seguido del número (ej: +526221234567)
                </p>
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Notificar al anular ventas</p>
                  <p className="text-sm text-muted-foreground">
                    Enviar mensaje cuando se anule una venta
                  </p>
                </div>
                <Switch
                  checked={whatsappConfig.notify_on_void}
                  onCheckedChange={(v) => setWhatsappConfig({ ...whatsappConfig, notify_on_void: v })}
                  data-testid="notify-void-switch"
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Notificar al editar ventas</p>
                  <p className="text-sm text-muted-foreground">
                    Enviar mensaje cuando se edite una venta
                  </p>
                </div>
                <Switch
                  checked={whatsappConfig.notify_on_edit}
                  onCheckedChange={(v) => setWhatsappConfig({ ...whatsappConfig, notify_on_edit: v })}
                  data-testid="notify-edit-switch"
                />
              </div>

              <div className="pt-2">
                <Button 
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleSaveWhatsApp}
                  disabled={savingWhatsApp}
                  data-testid="save-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {savingWhatsApp ? 'Guardando...' : 'Guardar Configuración WhatsApp'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Appearance Section */}
          <Card className="mb-6 border border-border/50">
            <CardHeader>
              <CardTitle className="font-chivo flex items-center gap-2">
                <Palette className="w-5 h-5" />
                Apariencia
              </CardTitle>
              <CardDescription>
                Personaliza la interfaz
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Modo Oscuro</p>
                  <p className="text-sm text-muted-foreground">
                    Usar tema oscuro en la interfaz
                  </p>
                </div>
                <Switch
                  checked={settings.appearance.darkMode}
                  onCheckedChange={(v) => {
                    setSettings({
                      ...settings,
                      appearance: { ...settings.appearance, darkMode: v }
                    });
                    // Toggle dark mode
                    document.documentElement.classList.toggle('dark', v);
                  }}
                />
              </div>
              
              <Separator />
              
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Vista Compacta</p>
                  <p className="text-sm text-muted-foreground">
                    Reducir espaciado para mostrar más información
                  </p>
                </div>
                <Switch
                  checked={settings.appearance.compactView}
                  onCheckedChange={(v) => setSettings({
                    ...settings,
                    appearance: { ...settings.appearance, compactView: v }
                  })}
                />
              </div>
            </CardContent>
          </Card>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              className="bg-accent text-white hover:bg-accent/90 shadow-lg shadow-accent/20"
              onClick={handleSave}
              disabled={saving}
              data-testid="save-settings-btn"
            >
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Settings;
